import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import TypewriterAnimation from "@default/general-components/typewriter-animation";
import classNames from "classnames";
import { motion } from "framer-motion";
import ErrorBoundary from "../../../error-boundary";
import DashedBorder from "@default/general-components/dashed-border";
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from "react-accessible-accordion";

const panelVariants = {
  open: { transition: { staggerChildren: 0.05, delayChildren: 0.05 } },
  closed: { transition: { staggerChildren: 0.02 } },
};

const itemVariants = {
  open: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { ease: "easeOut", duration: 0.2 },
  },
  closed: { opacity: 0, y: -6, filter: "blur(13px)" },
};

const contentWrapperVariants = {
  open: {
    height: "auto",
    transition: { duration: 0.25, ease: "easeInOut" },
  },
  closed: {
    height: 0,
    transition: { duration: 0.25, ease: "easeInOut" },
  },
};

/**
 * Recursive menu component that calls itself inside again if there
 * are subitems.
 * The level variable will count up to identify the nested layers.
 */
const RecursiveMenu = ({
  items,
  level = 1,
  itemIcon,
  blurEffect,
  treeLabel,
  isMainMenu,
  staggered,
}) => {
  const location = useLocation();
  const isMobile = useSelector((state) => state.appStore.isMobile);
  const [expandedUuid, setExpandedUuid] = useState(null);
  const itemRefs = useRef({});

  // Accordion only activates on mobile — avoids aria-hidden hiding content
  // from screen readers on desktop where the toggle button is not shown.
  const collapsibleOnMobile = isMainMenu && level === 1 && isMobile;

  const Li = staggered ? motion.li : "li";

  const list = (
    <ul className={`level-${level}`}>
      {treeLabel && (
        <li key="menu-label" className="menu-label">
          {treeLabel}
        </li>
      )}
      {items.map((item, i) => {
        const hasSubtree = !!item.subtree?.length;
        const uuid = `item-${i}`;
        const isOpen = expandedUuid === uuid;

        const linkEl = item.link.url.path ? (
          <NavLink
            to={item.link.url.path}
            title={item.link.description || item.link.label}
            aria-label={item.link.description || item.link.label}
            aria-current={
              item.link.url.path === location.pathname ? "page" : undefined
            }
            className={classNames({
              "parent-link": hasSubtree,
              "action-element": !hasSubtree,
              "highlighted d-none d-lg-flex": item.link.highlighted,
              standard: !item.link.highlighted,
            })}
            target={
              item?.link?.url?.__typename === "ExternalUrl" ? "_blank" : "_self"
            }
          >
            <TypewriterAnimation>
              {itemIcon} {item.link.label}
            </TypewriterAnimation>
          </NavLink>
        ) : (
          <span
            className={classNames("no-link", {
              highlighted: item.link.highlighted,
              standard: !item.link.highlighted,
              parent: hasSubtree,
            })}
          >
            {itemIcon} {item.link.label}
          </span>
        );

        return (
          <Li
            key={i + "-" + item.link.url.path}
            ref={(el) => {
              itemRefs.current[uuid] = el;
            }}
            className={classNames({
              "menu-block": level === 1,
              "hide-on-desktop d-lg-none": item.link.hideOnDesktop,
            })}
            variants={staggered ? itemVariants : undefined}
          >
            {collapsibleOnMobile && hasSubtree ? (
              <AccordionItem uuid={uuid}>
                <AccordionItemHeading>
                  <AccordionItemButton
                    className={classNames("item-header", "parent-link", {
                      highlighted: item.link.highlighted,
                      standard: !item.link.highlighted,
                    })}
                  >
                    {itemIcon} {item.link.label}
                  </AccordionItemButton>
                </AccordionItemHeading>
                <AccordionItemPanel>
                  <motion.div
                    className="content-wrapper"
                    initial="closed"
                    animate={isOpen ? "open" : "closed"}
                    variants={contentWrapperVariants}
                    onAnimationComplete={() => {
                      if (isOpen) {
                        itemRefs.current[uuid]?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }
                    }}
                  >
                    <motion.div
                      variants={panelVariants}
                      initial="closed"
                      animate={isOpen ? "open" : "closed"}
                    >
                      <ErrorBoundary>
                        <RecursiveMenu
                          items={item.subtree}
                          level={level + 1}
                          staggered
                        />
                      </ErrorBoundary>
                    </motion.div>
                  </motion.div>
                </AccordionItemPanel>
              </AccordionItem>
            ) : (
              <>
                {linkEl}
                {/* Recursively call menu component again if there are subitems */}
                {hasSubtree && (
                  <ErrorBoundary>
                    <RecursiveMenu items={item.subtree} level={level + 1} />
                  </ErrorBoundary>
                )}
              </>
            )}
          </Li>
        );
      })}
    </ul>
  );

  if (collapsibleOnMobile) {
    return (
      <Accordion
        allowMultipleExpanded={false}
        allowZeroExpanded
        onChange={(ids) => setExpandedUuid(ids[0] ?? null)}
      >
        {list}
      </Accordion>
    );
  }

  return list;
};

RecursiveMenu.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.shape({
        label: PropTypes.string,
        url: PropTypes.shape({
          path: PropTypes.string,
        }),
      }),
      subTree: RecursiveMenu.propTypes,
    })
  ),
  level: PropTypes.number,
  itemIcon: PropTypes.object,
  blurEffect: PropTypes.bool,
  isMainMenu: PropTypes.bool,
  staggered: PropTypes.bool,
};

export default RecursiveMenu;
